'use client'

import { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AdvancedMarker, APIProvider, Map, MapCameraChangedEvent } from '@vis.gl/react-google-maps'
import { cn, googleMapKeySelector, useLazyGetStoreListQuery, useMediaQuery } from '@segway/core'
import { StoreResult } from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useToggle } from 'ahooks'
import cloneDeep from 'lodash/cloneDeep'

import {
  Arrow,
  EmailIcon,
  EmptyStoreIcon,
  FilterIcon,
  HomeDeliveryIcon,
  LocationIcon,
  MapControls,
  MarkerIcon,
  PickIcon,
  RepairIcon,
  StoreAddressIcon,
  TelephoneIcon,
  TestRideIcon,
  TimeIcon,
} from '@/components'
import { Link } from '@/i18n/navigation'
import {
  Button,
  // Checkbox,
  Input,
  Skeleton,
} from '@/ui'

import './style.css'

export const ACTION_TYPE = {
  pickup: 'pick_up',
  testRide: 'test_ride',
  changeStore: 'change_store',
} as const

export type ActionType = (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE]

interface StoreMapProps {
  actionType: ActionType
  isHomeDelivery: boolean
  onSelectStore: (store: StoreResult, type?: boolean) => void
}

// const STORE_TYPE_OPTIONS = [
//   {
//     label: 'Pickup in store',
//     value: 'pickup',
//     icon: <PickIcon />,
//   },
//   {
//     label: 'Home Delivery',
//     value: 'service',
//     icon: <HomeDeliveryIcon />,
//   },
//   {
//     label: 'Test Ride',
//     value: 'test_drive',
//     icon: <TestRideIcon />,
//   },
//   {
//     label: 'Repair',
//     value: 'repair',
//     icon: <RepairIcon />,
//   },
// ]

const STORE_DISTANCE_OPTIONS = [
  {
    label: '20 Miles',
    value: '20',
  },
  {
    label: '50 Miles',
    value: '50',
  },
  {
    label: '100 Miles',
    value: '100',
  },
]

/**
 * 店铺地图
 */
const StoreMap: React.FC<StoreMapProps> = ({ actionType, isHomeDelivery, onSelectStore }) => {
  const [isFilterOpen, { toggle: toggleFilterOpen }] = useToggle(false)

  const googleMapKey = useAppSelector(googleMapKeySelector)
  const responsive = useMediaQuery()

  const [getStoreList] = useLazyGetStoreListQuery()
  const [isLoading, setIsLoading] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const storeListRef = useRef<HTMLDivElement>(null)
  const storeItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const [storeList, setStoreList] = useState<StoreResult[]>([])
  const [activeStore, setActiveStore] = useState<StoreResult | null>(null)
  const [activeStoreId, setActiveStoreId] = useState<number | null | undefined>(null)
  const [searchValue, setSearchValue] = useState('')
  const [storeType, setStoreType] = useState<(keyof StoreResult)[]>([])
  const [storeDistance, setStoreDistance] = useState('')
  const [mapState, setMapState] = useState({
    zoom: 10,
    center: { lat: 34.0313763, lng: -118.3740839 },
  })

  /**
   * 默认勾选
   */
  // const getDefaultChecked = useMemo(() => {
  //   if (actionType === ACTION_TYPE.pickup) {
  //     return isHomeDelivery ? 'service' : 'pickup'
  //   }
  //   if (actionType === ACTION_TYPE.testRide) {
  //     return 'test_drive'
  //   }

  //   return ''
  // }, [actionType, isHomeDelivery])

  /**
   * 过滤后门店列表
   */
  const filterStoreList = useMemo(() => {
    let filterTypeData = storeList
    if (storeType?.length > 0) {
      filterTypeData = storeList.filter((store) => {
        return storeType.every((type) => store[type] === '1')
      })
    }
    let filterDistanceData = filterTypeData
    if (storeDistance !== '') {
      filterDistanceData = filterTypeData.filter((store) => {
        if (store.distance_value === null) {
          return false
        } else {
          return Number(store.distance_value) <= Number(storeDistance)
        }
      })
    }
    return filterDistanceData
  }, [storeList, storeDistance, storeType])

  /**
   * 滚动到对应门店
   */
  const scrollToStoreItem = useCallback((storeId: number) => {
    const storeItem = storeItemRefs.current[storeId]
    const container = storeListRef.current

    if (storeItem && container) {
      const containerRect = container.getBoundingClientRect()
      const itemRect = storeItem.getBoundingClientRect()

      const scrollTo =
        itemRect.top -
        containerRect.top +
        container.scrollTop -
        containerRect.height / 2 +
        itemRect.height / 2

      container.scrollTo({
        top: scrollTo,
        behavior: 'smooth',
      })
    }
  }, [])

  /**
   * 点击marker
   */
  const handleMarkerClick = useCallback(
    (marker: StoreResult) => {
      if (marker.id !== activeStore?.id) {
        setActiveStore(marker)
        scrollToStoreItem(marker.id as number)
      }

      if (activeStoreId === marker.id) {
        setActiveStoreId(null)
      } else {
        setActiveStoreId(marker.id)
      }
    },
    [scrollToStoreItem, activeStoreId, activeStore],
  )

  /**
   * 默认勾选
   */
  useEffect(() => {
    if (actionType === ACTION_TYPE.pickup) {
      if (isHomeDelivery) {
        setStoreType(['service'])
      } else {
        setStoreType(['pickup'])
      }
    }
    if (actionType === ACTION_TYPE.testRide) {
      setStoreType(['test_drive'])
    }
  }, [actionType, isHomeDelivery])

  /**
   * 默认选中第一个门店
   */
  useEffect(() => {
    if (filterStoreList?.length > 0) {
      setActiveStore(filterStoreList[0])
      setActiveStoreId(filterStoreList[0].id)
    }
  }, [filterStoreList])

  useEffect(() => {
    if (activeStore) {
      setMapState({
        zoom: 10,
        center: {
          lat: Number(activeStore.latitude),
          lng: Number(activeStore.longitude),
        },
      })
    }
  }, [activeStore])

  /**
   * 获取门店列表参数
   */
  const fetchStoreParams = useCallback(
    (latAndLng: { lat?: number; lng?: number } = {}) => {
      if (searchValue) {
        return {
          address: searchValue,
        }
      }

      if (latAndLng?.lat && latAndLng?.lng) {
        return {
          latitude: String(latAndLng.lat),
          longitude: String(latAndLng.lng),
          address: '',
        }
      }

      return {}
    },
    [searchValue],
  )

  /**
   * 获取门店地址
   */
  const getStoreAddress = useCallback((store: StoreResult) => {
    const region = store.region?.code || store.region?.name
    const formattedAddress = [
      store.address,
      store.city,
      region + ' ' + store.zipcode,
      store.country_id,
    ]
      .filter((item) => {
        return !!item
      })
      .join(', ')

    return formattedAddress
  }, [])

  /**
   * 切换门店类型过滤
   */
  // const toggleStoreTypeFilter = useCallback(
  //   (checked: boolean | 'indeterminate', value: keyof StoreResult) => {
  //     if (checked) {
  //       setStoreType((prev) => [...prev, value])
  //     } else {
  //       setStoreType((prev) => prev.filter((type) => type !== value))
  //     }
  //   },
  //   [],
  // )

  /**
   * 门店距离过滤
   */
  const toggleStoreDistanceFilter = useCallback(
    (value: string) => {
      if (storeDistance === value) {
        setStoreDistance('')
      } else {
        setStoreDistance(value)
      }
    },
    [storeDistance],
  )

  /**
   * 获取门店列表
   */
  const fetchStoreList = useCallback(
    (latAndLng: { lat?: number; lng?: number } = {}) => {
      setIsLoading(true)
      getStoreList(fetchStoreParams(latAndLng))
        .unwrap()
        .then((res) => {
          if (res?.getStoreList) {
            let resultFiltered = cloneDeep(res?.getStoreList) || []

            // 对源数据先进行条件过滤
            if (actionType === 'pick_up' && resultFiltered.length > 0) {
              resultFiltered.forEach((store) => {
                if (store?.distance_value) {
                  const distance = +store.distance_value
                  if (distance < 50) {
                    store.service = '0'
                  }
                  if (distance > 100) {
                    store.pickup = '0'
                  }
                }
              })
              resultFiltered = resultFiltered.filter((store) => {
                return !(store?.service === '0' && store?.pickup === '0')
              })
            }
            setStoreList(resultFiltered as StoreResult[])
          }
        })
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [getStoreList, fetchStoreParams, actionType],
  )

  /**
   * 获取用户当前位置
   */
  useEffect(() => {
    setIsLoading(true)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        fetchStoreList({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      () => {
        fetchStoreList()
      },
      {
        // 可选配置项
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    )
  }, [fetchStoreList])

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden xl:h-auto xl:flex-row xl:rounded-lg">
      <div className="relative order-2 h-[70%] w-full overflow-hidden rounded-tl-[16px] rounded-tr-[16px] bg-white pl-[20px] xl:order-none xl:h-auto xl:w-[420px] xl:rounded-none">
        <div className="mr-[20px] mt-5 flex">
          <div className="flex-1">
            <Input
              ref={inputRef}
              placeholder="Enter Zip or Address"
              className="h-10 rounded-bl-[8px] rounded-br-[0px] rounded-tl-[8px] rounded-tr-[0px] border-none bg-gray-0 text-base xl:h-[47px]"
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  setSearchValue((e.target as HTMLInputElement).value)
                }
              }}
            />
          </div>
          <Button
            onClick={() => {
              if (inputRef.current?.value) {
                setSearchValue(inputRef.current.value)
              }
            }}
            className="h-10 w-10 rounded-bl-[0px] rounded-br-[8px] rounded-tl-[0px] rounded-tr-[8px] bg-black px-0 py-0 hover:bg-[#0F0F0F] focus:bg-[#000000] active:bg-[#000000] disabled:bg-[#BBBBBD] xl:h-[47px] xl:w-[51px]">
            {responsive && responsive.xl && <Arrow width={24} height={24} color="#ffffff" />}
            {responsive && !responsive.xl && <Arrow width={16} height={16} color="#ffffff" />}
          </Button>
        </div>

        <div
          onClick={toggleFilterOpen}
          className={cn(
            'mt-[20px] flex h-9 w-[80px] cursor-pointer items-center justify-center rounded-full bg-[#F3F3F4]',
            isFilterOpen ? 'border border-primary bg-white text-primary' : '',
          )}>
          <span
            className={cn(
              'mr-1 font-miSansMedium380 text-[14px] text-gray-8',
              isFilterOpen ? 'text-primary' : 'text-gray-8',
            )}>
            Filter
          </span>
          <FilterIcon color={isFilterOpen ? '#DA291C' : '#0F0F0F'} />
        </div>

        <div
          className={cn(
            isFilterOpen ? 'block' : 'hidden',
            'absolute left-[20px] top-[116px] rounded-lg bg-white p-[20px] shadow-[0px_0px_32px_0px_rgba(0,0,0,0.1)] xl:top-[130px]',
          )}>
          {/* <div className="space-y-[16px]">
            {STORE_TYPE_OPTIONS.map((option) => {
              return (
                <div key={option.value} className="flex items-center">
                  <Checkbox
                    className="rounded-full"
                    defaultChecked={getDefaultChecked === option.value}
                    disabled={getDefaultChecked === option.value}
                    onCheckedChange={(checked) =>
                      toggleStoreTypeFilter(checked, option.value as keyof StoreResult)
                    }
                  />
                  <div className="ml-4 mr-2">{option.icon}</div>
                  <p className="w-max">{option.label}</p>
                </div>
              )
            })}
          </div>
          <div className="my-4 h-[1px] bg-gray-2"></div> */}
          <p className="font-miSansDemiBold450 text-[14px] text-black">Search for Dealers within</p>
          <div className="flex items-center gap-3">
            {STORE_DISTANCE_OPTIONS.map((option) => {
              return (
                <div
                  onClick={() => toggleStoreDistanceFilter(option.value)}
                  className={cn(
                    'mt-4 flex h-9 w-[72px] cursor-pointer items-center justify-center rounded-[4px] bg-gray-1 font-miSansRegular330 text-[12px] text-gray-7',
                    storeDistance === option.value
                      ? 'border border-primary bg-[#FEE5E5] text-primary'
                      : '',
                  )}
                  key={option.value}>
                  {option.label}
                </div>
              )
            })}
          </div>
        </div>

        <div
          ref={storeListRef}
          className="store-list-container mr-[6px] mt-4 h-[calc(100%-150px)] overflow-y-auto pr-[6px] xl:h-[540px]">
          {!isLoading &&
            !!filterStoreList?.length &&
            filterStoreList.map((store) => (
              <div
                key={store.id}
                ref={(el) => {
                  if (el) {
                    storeItemRefs.current[store.id as number] = el
                  } else {
                    delete storeItemRefs.current[store.id as number]
                  }
                }}
                onClick={() => {
                  setActiveStore(store)
                  setActiveStoreId(store.id)
                }}
                className={cn(
                  'group mb-3 w-full cursor-pointer rounded-md border border-gray-2 p-[20px] hover:bg-gray-0',
                  activeStore?.id === store.id
                    ? 'border-2 border-primary p-[19px] hover:bg-transparent'
                    : '',
                )}>
                <div className="flex items-center justify-between">
                  <p className="line-clamp-1 max-w-[240px] font-miSansDemiBold450 text-xl text-gray-8">
                    {store.store_name}
                  </p>
                  {store.distance_text && (
                    <div className="flex items-center gap-1">
                      <LocationIcon />
                      <p className="font-miSansRegular330 text-base text-gray-4">
                        {store.distance_text}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-2 flex justify-between">
                  <p className="font-miSansRegular330 text-base text-gray-5 xl:max-w-[256px]">
                    {getStoreAddress(store)}
                  </p>
                  {responsive && responsive.xl && (
                    <div className="flex items-center gap-3">
                      <Link href={`tel:${store.telephone}`}>
                        <TelephoneIcon />
                      </Link>
                      <Link href={`mailto:${store.store_email}`}>
                        <EmailIcon />
                      </Link>
                    </div>
                  )}
                </div>
                <div className="my-3 h-[1px] bg-gray-1"></div>
                {responsive && responsive.xl && (
                  <div className="flex gap-2">
                    {!!store.store_time_label?.length && <TimeIcon />}
                    <div className="grid grid-cols-2 gap-2">
                      {store.store_time_label?.map((item, index) => (
                        <p key={index} className="font-miSansRegular330 text-[14px] text-gray-5">
                          {item}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {responsive && !responsive.xl && (
                  <div className="grid grid-cols-1 gap-1">
                    {store.store_time_label?.map((item, index) => (
                      <p key={index} className="font-miSansRegular330 text-[14px] text-gray-5">
                        {item}
                      </p>
                    ))}
                  </div>
                )}

                <div
                  className={`flex items-center justify-between ${activeStore?.id === store.id ? 'mt-4' : 'mt-4'}`}>
                  <div className="flex items-center gap-2">
                    {store.test_drive === '1' && (
                      <div
                        title="Test Ride"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                        <TestRideIcon />
                      </div>
                    )}
                    {store.repair === '1' && (
                      <div
                        title="Repair"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                        <RepairIcon />
                      </div>
                    )}
                    {store.service === '1' && (
                      <div
                        title="Home Delivery"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                        <HomeDeliveryIcon />
                      </div>
                    )}
                    {store.pickup === '1' && (
                      <div
                        title="Pickup In Store"
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-1">
                        <PickIcon />
                      </div>
                    )}
                  </div>

                  {actionType !== ACTION_TYPE.changeStore && (
                    <div className="relative h-9 w-[120px]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onSelectStore(activeStore as StoreResult)
                        }}
                        className={cn(
                          'absolute inset-0 flex h-9 w-[120px] items-center justify-center rounded-full border border-primary font-miSansMedium380 text-[12px] text-primary',
                          activeStore?.id === store.id
                            ? 'bg-primary text-white opacity-100'
                            : 'opacity-0 group-hover:bg-primary/5 group-hover:opacity-100',
                        )}>
                        Select this store
                      </button>
                    </div>
                  )}
                </div>

                {activeStore?.id === store.id && actionType === ACTION_TYPE.changeStore && (
                  <div className="mt-3 grid grid-cols-2 gap-[20px]">
                    {store.service === '1' && (
                      <button
                        onClick={() => onSelectStore(activeStore as StoreResult, true)}
                        className="flex h-9 w-full items-center justify-center rounded-full border border-primary font-miSansMedium380 text-[12px] text-primary">
                        Home Delivery
                      </button>
                    )}
                    {store.pickup === '1' && (
                      <button
                        onClick={() => onSelectStore(activeStore as StoreResult)}
                        className="flex h-9 w-full items-center justify-center rounded-full border border-primary font-miSansMedium380 text-[12px] text-primary">
                        Pick up In-Store
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

          {!isLoading && filterStoreList?.length === 0 && (
            <div className="my-[87px] flex flex-col items-center justify-center">
              <EmptyStoreIcon width={120} height={120} />
              <p className="mt-[-5px] font-miSansRegular330 text-base text-gray-4 xl:mt-0">
                No search results for now.
              </p>
            </div>
          )}

          {isLoading && (
            <div className="">
              <div className="mt-5">
                {new Array(4).fill(0).map((_, index) => (
                  <Skeleton
                    key={index}
                    className="mb-4 h-[145px] w-full rounded-[16px] bg-gray-0"></Skeleton>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="order-1 h-[30%] bg-gray-200 xl:order-none xl:h-auto xl:flex-1">
        {googleMapKey && (
          <APIProvider apiKey={googleMapKey}>
            <Map
              id="store-locator-map"
              mapId={process.env.NEXT_PUBLIC_GOOGLE_MAP_ID || 'store-locator-map'}
              center={mapState.center}
              zoom={mapState.zoom}
              onCameraChanged={(ev: MapCameraChangedEvent) => {
                setMapState({
                  center: ev.detail.center,
                  zoom: ev.detail.zoom,
                })
              }}
              mapTypeControl={false}
              fullscreenControl={false}
              streetViewControl={false}
              gestureHandling={'greedy'}
              disableDefaultUI={true}>
              {filterStoreList.length > 0 &&
                filterStoreList.map((marker) => (
                  <div key={marker.id} className="relative">
                    <AdvancedMarker
                      onClick={() => handleMarkerClick(marker)}
                      position={{ lat: Number(marker.latitude), lng: Number(marker.longitude) }}>
                      <MarkerIcon />
                      {responsive && responsive.xl && (
                        <div
                          className={cn(
                            'absolute -top-3 left-1/2 z-40 w-[310px] -translate-x-[50%] -translate-y-[100%] cursor-default rounded-lg bg-white/85 p-4 transition-opacity',
                            activeStoreId === marker.id ? 'opacity-100' : 'opacity-0',
                          )}>
                          <div className="absolute bottom-0 left-1/2 h-0 w-0 -translate-x-1/2 translate-y-full transform border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-white/85"></div>
                          <h4 className="test-gray-8 font-miSansDemiBold450 text-lg">
                            {marker?.store_name}
                          </h4>
                          <div className="mt-4 flex gap-3">
                            <div className="flex-none">
                              <StoreAddressIcon />
                            </div>
                            <p className="text-base text-gray-6">{getStoreAddress(marker)}</p>
                          </div>
                          <div className="mt-2 flex gap-3">
                            <EmailIcon />
                            <p className="text-base text-gray-6">{marker?.store_email}</p>
                          </div>
                          <div className="mt-2 flex gap-3">
                            <TelephoneIcon />
                            <p className="text-base text-gray-6">{marker?.telephone}</p>
                          </div>
                          <div className="mt-2 flex gap-3">
                            <TimeIcon />
                            <div className="grid gap-1">
                              {marker.store_time_label?.map((item, index) => (
                                <p
                                  key={index}
                                  className="font-miSansRegular330 text-base text-gray-6">
                                  {item}
                                </p>
                              ))}
                            </div>
                          </div>
                          <div className="mt-4 flex items-center gap-2">
                            {marker.test_drive === '1' && (
                              <div
                                title="Test Ride"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-2">
                                <TestRideIcon />
                              </div>
                            )}
                            {marker.repair === '1' && (
                              <div
                                title="Repair"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-2">
                                <RepairIcon />
                              </div>
                            )}
                            {marker.service === '1' && (
                              <div
                                title="Home Delivery"
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-2">
                                <HomeDeliveryIcon />
                              </div>
                            )}
                            {marker.pickup === '1' && (
                              <div
                                title="Pickup In Store"
                                className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-gray-2">
                                <PickIcon />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </AdvancedMarker>
                  </div>
                ))}
              <MapControls id="store-locator-map" />
            </Map>
          </APIProvider>
        )}
      </div>
    </div>
  )
}

export default StoreMap
